import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { movies } from "@/mocks/movies";
import MovieCard from "@/components/base/MovieCard";
import Pagination from "@/components/base/Pagination";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import FilterGroup from "@/pages/category/components/FilterGroup";

const CATEGORY_TABS = ["全部", "电影", "电视剧", "综艺", "纪录片"];
const SORTS = [
  { key: "latest", label: "最新", icon: "ri-time-line" },
  { key: "hot", label: "热门", icon: "ri-fire-line" },
  { key: "rating", label: "评分", icon: "ri-star-line" },
];
const PAGE_SIZE = 8;

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialCat = searchParams.get("cat") || "全部";

  const [category, setCategory] = useState(
    CATEGORY_TABS.includes(initialCat) ? initialCat : "全部"
  );
  const [genre, setGenre] = useState("");
  const [region, setRegion] = useState("");
  const [year, setYear] = useState("");
  const [sort, setSort] = useState("latest");
  const [page, setPage] = useState(1);

  useEffect(() => {
    document.title = "电影电视剧分类 - 泡泡影视";
  }, []);

  useEffect(() => {
    const cat = searchParams.get("cat");
    if (cat && CATEGORY_TABS.includes(cat)) setCategory(cat);
  }, [searchParams]);

  const baseMovies = useMemo(
    () => (category === "全部" ? movies : movies.filter((m) => m.category === category)),
    [category]
  );

  const genreOptions = useMemo(
    () => Array.from(new Set(baseMovies.flatMap((m) => m.genres))),
    [baseMovies]
  );
  const regionOptions = useMemo(
    () => Array.from(new Set(baseMovies.map((m) => m.region))),
    [baseMovies]
  );
  const yearOptions = useMemo(
    () =>
      Array.from(new Set(baseMovies.map((m) => String(m.year)))).sort(
        (a, b) => Number(b) - Number(a)
      ),
    [baseMovies]
  );

  const filtered = useMemo(() => {
    const result = baseMovies.filter((m) => {
      if (genre && !m.genres.includes(genre)) return false;
      if (region && m.region !== region) return false;
      if (year && String(m.year) !== year) return false;
      return true;
    });
    if (sort === "rating") return [...result].sort((a, b) => b.rating - a.rating);
    if (sort === "hot") return [...result].sort((a, b) => b.popularity - a.popularity);
    return [...result].sort((a, b) => b.year - a.year);
  }, [baseMovies, genre, region, year, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const resetFilters = () => {
    setGenre("");
    setRegion("");
    setYear("");
    setSort("latest");
    setPage(1);
  };

  const selectCategory = (cat: string) => {
    setCategory(cat);
    setGenre("");
    setRegion("");
    setYear("");
    setPage(1);
    setSearchParams(cat === "全部" ? {} : { cat });
  };

  return (
    <div className="w-full px-4 md:px-8 pt-6">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "影视分类" }]} />

      <div className="mt-5 rounded-xl bg-primary-950 px-6 py-8 md:px-10 md:py-10">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-background-50">
          影视分类浏览
        </h1>
        <p className="mt-2 text-sm md:text-base text-background-300 max-w-2xl leading-relaxed">
          按影片类型、地区与年份筛选，自由排序，快速找到你感兴趣的电影、电视剧、综艺与纪录片。
        </p>
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        {CATEGORY_TABS.map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => selectCategory(tab)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
              category === tab
                ? "bg-primary-600 text-background-50"
                : "bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-700"
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      <div className="mt-5 rounded-lg border border-background-200 bg-background-50 px-4 md:px-6 py-2">
        <FilterGroup label="类型" options={genreOptions} value={genre} onChange={(v) => { setGenre(v); setPage(1); }} />
        <FilterGroup label="地区" options={regionOptions} value={region} onChange={(v) => { setRegion(v); setPage(1); }} />
        <FilterGroup label="年份" options={yearOptions} value={year} onChange={(v) => { setYear(v); setPage(1); }} />
      </div>

      <div className="mt-5 flex items-center justify-between gap-4">
        <p className="text-sm text-foreground-500">
          共找到 <span className="font-semibold text-foreground-900">{filtered.length}</span> 部作品
        </p>
        <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
          {SORTS.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => { setSort(item.key); setPage(1); }}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                sort === item.key
                  ? "bg-background-50 text-primary-700 font-medium"
                  : "text-foreground-600 hover:text-primary-700"
              }`}
            >
              <i className={item.icon} />
              {item.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-5">
        {pageItems.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {pageItems.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        ) : (
          <EmptyState
            title="暂无符合条件的影视资源"
            description="试着放宽筛选条件，或者直接重置全部筛选，看看其他精彩内容。"
            actionLabel="重置筛选条件"
            onAction={resetFilters}
          />
        )}
      </div>

      {pageItems.length > 0 && (
        <div className="mt-10">
          <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
        </div>
      )}
    </div>
  );
}